/**
 * Labor-Smoke: `?demoFrist=1` **ohne** angemeldete Portal-Session.
 * Lokaler Mock für Approval-Card + PB-Liste — kein n8n/Motor.
 *
 * Mit PB-Token (echter Portal-Login): gleiche URL-Flag, aber echte Webhooks;
 * Vorschau-Ende wird per Motor-Preview (`action: preview`) nachgeladen.
 *
 * Unterscheidungsmerkmal war früher das Upload-Secret im Bundle. Seit dem
 * Secret-Cutover hängt der echte Pfad am PB-Token: kein Token = kein Zugang zu
 * `/webhook/portal-frist-approval`, also Mock.
 */

import { getPortalNcMandantenRoot, hasPortalAuthzToken } from '../../../constants';
import type { ApprovalFristPayload } from './fristApprovalTypes';

/** Ab dieser Laenge gilt ein geoeffnetes Dokument als „echt“ — Demo-Card wird unterdrueckt. */
export const DEMO_FRIST_REAL_DOC_MIN_LEN = 200;

const DEMO_DOC_PATH_RE = /Akte-Frist-Demo|Beschluss-Demo/i;

export function isDemoFristUrlEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('demoFrist') === '1';
}

export function isExplicitDemoDocumentPath(documentContext?: string | null): boolean {
  if (!documentContext?.trim()) return false;
  return DEMO_DOC_PATH_RE.test(documentContext);
}

export function hasSubstantialDocumentContext(documentContext?: string | null): boolean {
  return (documentContext?.trim().length ?? 0) > DEMO_FRIST_REAL_DOC_MIN_LEN;
}

/**
 * Demo-Card nur bei `?demoFrist=1` ohne echtes Produktiv-Dokument.
 * Geoeffneter Mietvertrag/Notiz (>200 Zeichen) blockiert Demo — auch mit URL-Flag.
 */
export function shouldAllowDemoFristCard(documentContext?: string | null): boolean {
  if (!isDemoFristUrlEnabled()) return false;
  if (isExplicitDemoDocumentPath(documentContext)) return true;
  if (hasSubstantialDocumentContext(documentContext)) return false;
  return true;
}

/** True wenn Webhook-Aktionen lokal gemockt werden (kein PB-Token, URL-Flag gesetzt). */
export function isPortalFristApprovalDemoMode(): boolean {
  if (hasPortalAuthzToken()) return false;
  return isDemoFristUrlEnabled();
}

/** `?demoFrist=1` mit Portal-Login — Demo-Card, Motor-Vorschau über n8n preview. */
export function isDemoFristMotorPreviewMode(): boolean {
  if (!hasPortalAuthzToken()) return false;
  return isDemoFristUrlEnabled();
}

export function buildDemoApprovalFristPayload(
  customerStatus?: string,
  customerEmail?: string,
): ApprovalFristPayload {
  const root = getPortalNcMandantenRoot(customerStatus, customerEmail);
  const akte = `/${root}/Akte-Frist-Demo/`;
  const doc = `${akte}Beschluss-Demo.pdf`;
  const today = new Date().toISOString().slice(0, 10);

  return {
    card_id: `demo-frist-${today}`,
    approval_kind: 'frist_vorschlag',
    frist_typ_vorschlag: 'Berufung (ZPO)',
    start_datum: '2026-07-01',
    ende_vorschau: '2026-07-15',
    kalender_trace:
      'Demo: 14 Tage ab Zustellung; Wochenende übersprungen (lokaler Mock ohne n8n).',
    regel_id: 'berufung_zpo_v1',
    akte_nc_path: akte,
    quell_dokument_nc_path: doc,
    begruendung_kurz: hasPortalAuthzToken()
      ? 'Labor-Smoke: ?demoFrist=1 — Vorschau per Verfahrensmotor (gleich wie nach Bestätigung).'
      : 'Labor-Smoke: ?demoFrist=1 ohne Portal-Token — nur UI/Mock.',
  };
}

export function demoFristStorageKey(customerId: string): string {
  return `arkorbit-demo-fristen:${customerId}`;
}
